import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../index';
import {
  Overlay,
  Container,
  Header,
  Title,
  CloseButton,
  Subtitle,
  Body,
  Sidebar,
  SidebarList,
  SidebarItem,
  Content,
  CollapsibleIndex,
} from './styled';
import CloseIcon from '@mui/icons-material/Close';

const IUBENDA_API_URL =
  'https://www.iubenda.com/api/termini-e-condizioni/17860076';

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

interface SidebarItemType {
  id: string;
  label: string;
}

interface ModalTerminiCondizioniProps {
  open: boolean;
  onClose: () => void;
}

export const ModalTerminiCondizioni: React.FC<ModalTerminiCondizioniProps> = ({
  open,
  onClose,
}) => {
  const [active, setActive] = useState('');
  const [showIndex, setShowIndex] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const [sidebar, setSidebar] = useState<SidebarItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(IUBENDA_API_URL)
      .then((r) => r.json())
      .then((data) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.content, 'text/html');
        // Prendi solo gli h2 principali per la sidebar
        const headings = Array.from(doc.querySelectorAll('h2'));
        const sidebarItems: SidebarItemType[] = headings.map((h, idx) => {
          let id = h.getAttribute('id');
          if (!id) {
            id = slugify(h.textContent || '') + '-' + idx;
            h.setAttribute('id', id);
          }
          return { id, label: h.textContent || '' };
        });
        setSidebar(sidebarItems.filter((i) => i.id && i.label));
        setHtml(doc.body.innerHTML);
        setActive(sidebarItems[0]?.id || '');
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [open]);

  // Scrollspy: aggiorna item attivo in base allo scroll
  useEffect(() => {
    if (!html) return;
    const handler = () => {
      const headings = sidebar
        .map((s) => document.getElementById(s.id))
        .filter(Boolean) as HTMLElement[];
      const scrollY = contentRef.current?.scrollTop || 0;
      let current = sidebar[0]?.id;
      for (const h of headings) {
        if (h.offsetTop - 60 <= scrollY) {
          current = h.id;
        }
      }
      setActive(current || '');
    };
    const el = contentRef.current;
    if (el) el.addEventListener('scroll', handler);
    return () => {
      if (el) el.removeEventListener('scroll', handler);
    };
  }, [html, sidebar]);

  const handleSidebarClick = (id: string) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el && contentRef.current) {
      const targetTop = el.offsetTop - 16;
      // JSDOM non implementa `scrollTo`, quindi evitiamo crash in test
      // e usiamo un fallback su `scrollTop`.
      if (typeof contentRef.current.scrollTo === 'function') {
        contentRef.current.scrollTo({
          top: targetTop,
          behavior: 'smooth',
        });
      } else {
        contentRef.current.scrollTop = targetTop;
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="" small={false}>
      <Overlay>
        <Container>
          <Header>
            <Title>Terms and Conditions</Title>
            <Subtitle>Last modified: July 16, 2025</Subtitle>
            <CloseButton onClick={onClose} aria-label="Close">
              <CloseIcon />
            </CloseButton>
          </Header>
          <Body>
            <Sidebar>
              <SidebarList>
                {sidebar.map((c) => (
                  <SidebarItem
                    key={c.id}
                    active={active === c.id}
                    onClick={() => handleSidebarClick(c.id)}
                  >
                    {c.label}
                  </SidebarItem>
                ))}
              </SidebarList>
            </Sidebar>
            <Content ref={contentRef}>
              <CollapsibleIndex>
                <button onClick={() => setShowIndex((v) => !v)}>
                  Index {showIndex ? '▲' : '▼'}
                </button>
                {showIndex && (
                  <SidebarList>
                    {sidebar.map((c) => (
                      <SidebarItem
                        key={c.id}
                        active={active === c.id}
                        onClick={() => handleSidebarClick(c.id)}
                      >
                        {c.label}
                      </SidebarItem>
                    ))}
                  </SidebarList>
                )}
              </CollapsibleIndex>
              {loading && <div style={{ padding: 32 }}>Loading…</div>}
              {error && (
                <div style={{ color: 'red', padding: 32 }}>
                  Error loading Terms.{' '}
                  <a
                    href="https://www.iubenda.com/termini-e-condizioni/17860076"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in new tab
                  </a>
                </div>
              )}
              {!loading && !error && html && (
                <div dangerouslySetInnerHTML={{ __html: html }} />
              )}
            </Content>
          </Body>
        </Container>
      </Overlay>
    </Modal>
  );
};

export default ModalTerminiCondizioni;
